import { useState, useEffect, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { useFloating, autoUpdate, shift, OffsetOptions, offset, ShiftOptions } from '@floating-ui/react';
import { useHotkeys } from 'react-hotkeys-hook';
import { FILE_LIST_DEFAULT_FLOATING_SHIFT } from '../constants';
import { MessagingExpandableTagListItem } from '../types';

export interface UseMessageExpandableTagListArgs {
  expandedTagListID?: string;
  verticalPlacement: 'top' | 'bottom';
  tagItems: MessagingExpandableTagListItem[];
  floatingOverrides?: { offsetMiddleware?: OffsetOptions; shiftMiddleware?: ShiftOptions };
  setExpandedTagListID?: (tagListID: string | undefined) => void;
}

export function useMessageExpandableTagList({
  tagItems,
  floatingOverrides,
  verticalPlacement,
  expandedTagListID,
  setExpandedTagListID,
}: UseMessageExpandableTagListArgs) {
  const tagListID = useId();
  const [floatingHeight, setFloatingHeight] = useState(0);
  const { t: tMessaging } = useTranslation('inAppMessaging');

  const expanded = expandedTagListID === tagListID;

  useHotkeys('esc', () => setExpandedTagListID?.(undefined), { enabled: expanded });

  const {
    refs,
    x: floatingX,
    y: floatingY,
  } = useFloating({
    open: expanded,
    whileElementsMounted: autoUpdate,
    placement: verticalPlacement === 'top' ? 'bottom-start' : 'top-start',
    onOpenChange: (open) => setExpandedTagListID?.(open ? tagListID : undefined),
    middleware: [
      offset(floatingOverrides?.offsetMiddleware),
      shift(floatingOverrides?.shiftMiddleware ?? { crossAxis: true, padding: FILE_LIST_DEFAULT_FLOATING_SHIFT }),
    ],
  });

  const reversedTagItems = tagItems?.slice().reverse();
  const thumbnailTagItems = reversedTagItems?.slice(0, 2);

  useEffect(() => {
    if (refs.floating.current && !expanded) {
      setFloatingHeight(refs.floating.current.offsetHeight);
    }
  }, [expanded, refs.floating.current]);

  // Sync state of expanded with files length
  useEffect(() => {
    if (expanded && tagItems.length <= 2) {
      setExpandedTagListID?.(undefined);
    }
  }, [tagItems.length, expanded, tagListID]);

  return {
    refs,
    reversedTagItems,
    thumbnailTagItems,
    tMessaging,
    floatingHeight,
    floatingX,
    floatingY,
    expanded,
    tagListID,
  };
}
